import { X } from 'lucide-react';
import type { CalculatedMetric, RagStatus, MetricGoal } from '@monitoring-panel/types';
import { FIXED_METRICS } from '@monitoring-panel/types';
import { formatNumber, cn } from '@/lib/utils';
import { RAG_STYLES } from '@/lib/advia-status';

interface MetricsTableProps {
  metrics: CalculatedMetric[];
  onRemoveMetric: (metricId: string) => void;
  starredMetrics?: string[];
  metricGoals?: Record<string, MetricGoal>;
}

const fixedMetricIds = FIXED_METRICS.map((m) => m.id);

function ragRowClass(status?: RagStatus): string {
  return status ? RAG_STYLES[status].background : '';
}

function hasRowBackground(rowStatus?: RagStatus): boolean {
  return rowStatus !== undefined && rowStatus !== 'none';
}

/** Gold text only reads on a neutral row; on a tinted row amber falls back to bold graphite. */
function ragTextClass(status?: RagStatus, rowStatus?: RagStatus): string {
  if (!status) return '';
  if (status === 'amber' && hasRowBackground(rowStatus)) return 'text-adv-primary font-bold';
  return RAG_STYLES[status].text;
}

function formatGoalDisplay(goal: MetricGoal | undefined, format: string, decimals?: number): string {
  if (!goal) return '-';

  const formattedTarget = formatNumber(goal.target, format as any, decimals);

  switch (goal.direction) {
    case 'higher_is_better':
      return `≥${formattedTarget}`;
    case 'lower_is_better':
      return `≤${formattedTarget}`;
    case 'close_to_target':
      return `~${formattedTarget}`;
    default:
      return formattedTarget;
  }
}

export function MetricsTable({
  metrics,
  onRemoveMetric,
  starredMetrics = [],
  metricGoals = {},
}: MetricsTableProps) {
  // Determine if we have yesterday data for any metric
  const hasYesterdayData = metrics.some((m) => m.yesterday !== null);
  const dailyColumnLabel = hasYesterdayData ? 'Yesterday' : 'Last Day';

  return (
    <div className="border-t border-adv-border overflow-x-auto">
      <table className="adv-table adv-table--compact min-w-[400px]">
        <thead>
          <tr className="text-left">
            <th className="py-2 px-2 min-w-[100px]">Metric</th>
            <th className="py-2 px-2 text-right">KPI</th>
            <th className="py-2 px-2 text-right">ACC</th>
            <th className="py-2 px-2 text-right">{dailyColumnLabel}</th>
            <th className="py-2 px-2 text-right">RR</th>
            <th className="py-2 px-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => {
            const isFixed = fixedMetricIds.includes(metric.id);
            const rowRagStatus = metric.ragStatus?.runRate;
            const isStarred = starredMetrics.includes(metric.id);
            const goal = metricGoals[metric.id];
            const dailyValue = hasYesterdayData ? metric.yesterday : metric.lastDay;
            const dailyRagStatus = hasYesterdayData
              ? metric.ragStatus?.yesterday
              : metric.ragStatus?.lastDay;

            return (
              <tr key={metric.id} className={ragRowClass(rowRagStatus)}>
                <td className={cn('py-2 px-2', ragTextClass(metric.ragStatus?.accumulated, rowRagStatus), isStarred && 'font-bold')}>
                  {metric.name}
                </td>
                <td className={cn('py-2 px-2 text-right adv-num text-adv-secondary whitespace-nowrap')}>
                  {formatGoalDisplay(goal, metric.format, metric.decimals)}
                </td>
                <td className={cn('py-2 px-2 text-right adv-num whitespace-nowrap', ragTextClass(metric.ragStatus?.accumulated, rowRagStatus))}>
                  {formatNumber(metric.accumulated, metric.format, metric.decimals)}
                </td>
                <td className={cn('py-2 px-2 text-right adv-num whitespace-nowrap', ragTextClass(dailyRagStatus, rowRagStatus))}>
                  {formatNumber(dailyValue, metric.format, metric.decimals)}
                </td>
                <td className={cn('py-2 px-2 text-right adv-num whitespace-nowrap', ragTextClass(rowRagStatus, rowRagStatus))}>
                  {formatNumber(metric.runRate, metric.format, metric.decimals)}
                </td>
                <td className="py-2 px-2 text-center">
                  {!isFixed && (
                    <button
                      onClick={() => onRemoveMetric(metric.id)}
                      className="text-adv-tertiary hover:text-adv-warning transition-colors"
                      title="Remove metric"
                    >
                      <X size={14} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
